import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetch, useNotificationContext } from "@4uhub/lib4uhub";

import { ScheduleAppointmentService } from "../../../../Schedule.service";
import { ETasyScheduleTypeCode } from "../../../../model";
import { TAppointment } from "../../schema";

export const useConfirm = (reschedule: boolean = false) => {
  const [loading, setLoading] = useState(false);

  const { setMessage } = useNotificationContext();

  const { t } = useTranslation();

  const { sendRequest: SchedConsultation, error: consultationError } = useFetch(
    ScheduleAppointmentService.calendarConsultationScheduling
  );

  const { sendRequest: SchedExam, error: examError } = useFetch(
    ScheduleAppointmentService.calendarExamScheduling
  );

  const { sendRequest: cancelExam } = useFetch(
    ScheduleAppointmentService.calendarExamScheduleCancellation
  );

  const { sendRequest: cancelConsultation } = useFetch(
    ScheduleAppointmentService.calendarConsultationScheduleCancellation
  );

  const cancelSchedule = useCallback(
    async (v: TAppointment) => {
      await (v.search.type === ETasyScheduleTypeCode.CONSULTATION
        ? cancelConsultation
        : cancelExam)({
        calendarDate: v.search.date.toString(),
        cancellationReasonCode: "1",
        cancellationReasonDescription: "Reagendamento",
        identifier: String(v.schedule.identifier),
        identifierCalendar:
          v.search.type === ETasyScheduleTypeCode.CONSULTATION
            ? String(v.search.professional?.id)
            : String(v.search.examGroup.id),
      });
    },
    [cancelConsultation, cancelExam]
  );

  const scheduleConsultation = useCallback(
    async (v: TAppointment) => {
      setLoading(true);

      if (reschedule) {
        await cancelSchedule(v);
      }

      const { success } = await (v.search.type ===
        ETasyScheduleTypeCode.CONSULTATION
        ? SchedConsultation
        : SchedExam)({
        identifier: v.schedule.identifier,
        insuranceCode: v.insurance.id,
        pacientIndividualIdentifier: v.patient.id,
        phoneNumberContact: v.phoneNumberContact,
        isWhatsappPhoneNumberContact: v.isWhatsappPhoneNumberContact,
      });

      if (success) {
        setMessage({
          message: t("components.scheduleAssistant.newSchedule.success"),
          type: "success",
        });
      }

      setLoading(false);
      return success;
    },
    [SchedConsultation, SchedExam, setMessage, t, reschedule, cancelSchedule]
  );

  const error = useMemo(
    () => consultationError || examError,
    [consultationError, examError]
  );

  return {
    scheduleConsultation,
    loading,
    error,
  };
};
